import type { MimeTypeEnum } from '@/graphql/types';
import ClearIcon from '@mui/icons-material/Clear';
import SentimentVeryDissatisfiedIcon from '@mui/icons-material/SentimentVeryDissatisfied';
import IconButton from '@mui/material/IconButton';
import React, { useEffect, useMemo } from 'react';
import CsvRenderer from './components/csv-renderer';

const UnsupportedElement = () => {
  return (
    <div>
      <SentimentVeryDissatisfiedIcon sx={{ fontSize: '10em' }} />
      <p>{`Sorry, this file isn't supported`}</p>
    </div>
  );
};

const getMediaElement = (
  file: File,
  mimeTypes?: MimeTypeEnum[]
): { url: string; element: React.JSX.Element; supported: boolean } => {
  let element = <div>unknown</div>;
  let url = '';

  const mimeType = file.type.split('/')[0];

  // converts from form like 'video/mp4' to 'VIDEO_MP4'
  const convertedMime = file.type.replace(/[./-]/, '_').toUpperCase();

  const supportedTypes: MimeTypeEnum[] | string[] = mimeTypes || [
    'image/png',
    'image/webp',
    'image/jpg',
    'image/jpeg',
    'image/gif',
    'video/mp4',
    'video/webm',
    'audio/mp3',
    'application/pdf',
    'text/csv',
  ];
  const supportedFile = supportedTypes.indexOf((mimeTypes ? convertedMime : file.type) as MimeTypeEnum) > -1;

  if (!supportedFile)
    return {
      url: '',
      element: (
        <>
          <UnsupportedElement />
        </>
      ),
      supported: false,
    };

  if (supportedFile) url = URL.createObjectURL(file); // create url from file

  switch (mimeType) {
    case 'image':
      element = <img style={{ maxWidth: '100%' }} src={url} />;
      break;
    case 'video':
      element = <video style={{ maxWidth: '100%' }} src={url} controls />;
      break;
    case 'audio':
      element = (
        <audio controls>
          <source src={url} type={file.type} />
        </audio>
      );
      break;
    case 'application':
      if (file.type === 'application/pdf')
        element = (
          <iframe
            // @ts-ignore
            credentialless="true"
            crossOrigin="use-credentials"
            title="pdf"
            className="zn-pdf-iframe"
            src={url}
            width="100%"
            height="400px"
            style={{ border: '0px' }}
          />
        );
      break;
    case 'text':
      if (file.type === 'text/csv') {
        element = (
          <CsvRenderer
            file={file}
            muiTableContainerProps={{
              sx: {
                maxHeight: '300px',
              },
            }}
          />
        );
      }
      break;
    default:
      element = <div>unknown</div>;
  }

  return { url, element, supported: true };
};

type Props = {
  file: File;
  onClear?: () => void;
  mimeTypes?: MimeTypeEnum[];
  setSupported?: (isSupported: boolean) => void;
};

const FilePreview = ({ file, onClear, mimeTypes, setSupported }: Props) => {
  const { url, element, supported } = useMemo(() => getMediaElement(file, mimeTypes), [file]);

  useEffect(() => {
    setSupported?.(supported);
  }, [supported]);

  // if file isnt type of file then clear
  if (typeof file !== 'object' || !file) {
    onClear?.();
    return <></>;
  }

  return (
    <div style={{ position: 'relative', width: '100%', textAlign: 'center' }}>
      {element}

      {/* clear the current preview button */}
      {onClear && (
        <IconButton
          style={{
            position: 'absolute',
            top: 0,
            right: 0,
            background: 'white',
            borderRadius: 'unset',
            zIndex: 1,
          }}
          onClick={() => {
            url && URL.revokeObjectURL(url); // remove blob
            onClear();
          }}
        >
          <ClearIcon />
        </IconButton>
      )}
    </div>
  );
};

export default React.memo(FilePreview);
